import type { FavoriteStatusModel, ProductAction } from 'autogen/swagger/all';
import ProductFavoriteButton from 'components/generic/productFavoriteButton/ProductFavoriteButton';
import styled, { css } from 'styled-components';
import { ColorNordicGrey4, Spacing8 } from 'autogen/design-tokens/tokens';
import type { IProductActionButton } from 'components/generic/productCTA/shared/types';
import PDPProductActionRenderer from './PDPProductActionRenderer';
import { Button } from 'components/Button';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import { useTranslation } from 'react-i18next';
import { GoWishAriaText } from 'autogen/translation-keys/trans-website-product';
import useSegmentation from 'hooks/user/useSegmentation';
import { useEffect } from 'react';
import useFeatureToggles from 'hooks/user/useFeatureToggles';
import { useIsChanel } from 'hooks/user/useIsBrand';
import useRequestContext from 'hooks/user/useRequestContext';

const Wrapper = styled.div<{ $isOenskeskyenEnabled: boolean }>`
    display: grid;
    grid-template-columns:  ${({ $isOenskeskyenEnabled }) => ($isOenskeskyenEnabled ? '1fr 48px 48px' : '1fr 48px')};
    gap: ${Spacing8};
`;

const StyledPDPProductActionRenderer = styled(PDPProductActionRenderer)`
    grid-column: 1;
`;

const StyledProductFavoriteButton = styled(ProductFavoriteButton)<{ $chanelStyling: boolean }>`
    grid-column: 2;
    grid-row: 1;

    ${({ $chanelStyling }) =>
        $chanelStyling &&
        css`
            border-radius: 0;
        `}
`;

const StyledButton = styled(Button)`
    grid-column: 3;
    grid-row: 1;
    background-image: url(/images/GoWish.svg);
    background-repeat: no-repeat;
    background-size: 24px;
    background-position: center center;
`;

export default function CombinedProductActions({
    action,
    productActionModel,
    favoriteModel,
    ...restProps
}: {
    action: ProductAction;
    productActionModel: Omit<IProductActionButton['model'], 'action'>;
    favoriteModel: FavoriteStatusModel;
}): JSX.Element {
    const { favoriteStatus, productId } = favoriteModel;
    const { onskeskyenEnabled, onskeskyenEnabledInApp } = useFeatureToggles();
    const { isClubMatasApp } = useRequestContext();
    const isChanel = useIsChanel();
    const { t } = useTranslation();
    const { countryName } = useSegmentation();
    const model = { action, ...productActionModel };

    const showOnskeskyen = isClubMatasApp ? onskeskyenEnabled && onskeskyenEnabledInApp : onskeskyenEnabled;

    function loadGoWishScript() {
        return new Promise((resolve, reject) => {
            const id = 'gowish-iframescript';
            const existingScript = document.getElementById(id);

            if (!existingScript) {
                const script = document.createElement('script');
                script.id = id;
                script.src = 'https://storage.googleapis.com/gowish-button-prod/js/gowish-iframe.js';
                script.async = true;
                script.setAttribute('data-region', countryName.toLowerCase());
                script.onload = () => resolve(script);
                script.onerror = () => reject(new Error('Failed to load GoWish script'));
                const firstScript = document.getElementsByTagName('script')[0];
                firstScript.parentNode?.insertBefore(script, firstScript);
            }
        });
    }

    const initGoWish = async () => {
        if (showOnskeskyen && !isChanel) {
            try {
                await loadGoWishScript();
            } catch (error) {
                console.error('Error loading or initializing the plugin', error);
            }
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        initGoWish();
    }, []);

    return (
        <Wrapper {...restProps} $isOenskeskyenEnabled={showOnskeskyen}>
            <StyledPDPProductActionRenderer size="lg" model={model} key={action} />

            <StyledProductFavoriteButton
                size="lg"
                iconSize="lg"
                variant="secondary"
                productId={productId}
                initialFavoriteState={favoriteStatus === 'IsFavorite'}
                uaTrackingCategory={productActionModel.trackingContext.eventCategory}
                $chanelStyling={isChanel}
            />

            {showOnskeskyen && !isChanel && (
                <StyledButton
                    size="lg"
                    variant="secondary"
                    aria-label={t(GoWishAriaText)}
                    id="gowishWhiteButton"
                    onClick={() => {
                        GA4Tracking({
                            eventName: 'add_to_oenskeskyen',
                            context: 'product_page',
                            category: 'ecommerce'
                        });
                    }}
                />
            )}
        </Wrapper>
    );
}
